import React, { useEffect, useRef, useState, useLayoutEffect, useMemo } from "react";
import { Howl, Howler } from "howler";
import startAudioMp3 from "utils/assets/audio/start_audio.mp3";
import endAudioMp3 from "utils/assets/audio/end_audio.mp3";

const rand = (m, M) => Math.random() * (M - m) + m;

const SPIN_DURATION_MS = 2200;
const MIN_FULL_ROTATIONS = 4.5;
const MAX_FULL_ROTATIONS = 6.5;
const TEXT_SUPERSAMPLE_FACTOR = 2;
const MAX_RENDER_SCALE = 4;
const AUDIO_UPDATE_INTERVAL_MS = 50;
const MAX_EXPECTED_ANGULAR_VELOCITY = 0.9;
const PLAYBACK_RATE_MIN = 0.55;
const PLAYBACK_RATE_MAX = 2.2;
const VOLUME_FADE_THRESHOLD = 0.12;

const TAU_MATH = 2 * Math.PI;

const WheelComponent = ({ segments, onFinished, dimensions, newStudentList }) => {
    const [result, setResult] = useState(null);
    const [devicePixelRatio, setDevicePixelRatio] = useState(() => window.devicePixelRatio || 1);
    const isActiveRef = useRef(true);
    const runningAudioRef = useRef(null);
    const endAudioRef = useRef(null);
    const endAudioFallbackRef = useRef(null);
    const endAudioRetryTimeoutRef = useRef(null);
    const spinCompletionTimeoutRef = useRef(null);
    const drawWheelRef = useRef(() => {});
    const completeSpinRef = useRef(() => {});
    const spinStateRef = useRef({
        ang: 0,
        startAng: 0,
        targetAng: 0,
        spinWallStartMs: 0,
        durationMs: SPIN_DURATION_MS,
        isSpinning: false,
        animFrame: null,
        hasCompleted: false,
        prevAngForAudio: 0,
        audioLastTs: 0,
    });

    const canvasRef = useRef(null);
    const ctxRef = useRef(null);
    const wheelBufferRef = useRef(null);

    const logicalWheelSize = useMemo(() => {
        const h = dimensions?.height;
        if (h == null) return 400;
        if (300 < h && h < 791) return Math.max(1, Math.round(h - 150));
        if (791 < h && h < 1529) return Math.max(1, Math.round(h - 300));
        if (h > 1647) return 1317;
        return 400;
    }, [dimensions?.height]);

    const tot = segments?.length ?? 0;
    const dia = logicalWheelSize;
    const rad = dia / 2;
    const PI = Math.PI;
    const TAU = 2 * PI;
    const arc = tot > 0 ? TAU / tot : 0;

    useEffect(() => {
        runningAudioRef.current = new Howl({
            src: [startAudioMp3],
            loop: true,
            volume: 1,
            html5: false,
            preload: true,
        });
        endAudioRef.current = new Howl({
            src: [endAudioMp3],
            loop: false,
            volume: 1,
            html5: false,
            preload: true,
        });
        endAudioFallbackRef.current = new Audio(endAudioMp3);
        endAudioFallbackRef.current.preload = "auto";
        return () => {
            isActiveRef.current = false;
            if (runningAudioRef.current) {
                runningAudioRef.current.stop();
                runningAudioRef.current.unload();
                runningAudioRef.current = null;
            }
            if (endAudioRef.current) {
                endAudioRef.current.stop();
                endAudioRef.current.unload();
                endAudioRef.current = null;
            }
            if (endAudioFallbackRef.current) {
                endAudioFallbackRef.current.pause();
                endAudioFallbackRef.current.currentTime = 0;
                endAudioFallbackRef.current = null;
            }
            const { animFrame } = spinStateRef.current;
            if (animFrame != null) cancelAnimationFrame(animFrame);
            if (spinCompletionTimeoutRef.current != null) {
                clearTimeout(spinCompletionTimeoutRef.current);
                spinCompletionTimeoutRef.current = null;
            }
            if (endAudioRetryTimeoutRef.current != null) {
                clearTimeout(endAudioRetryTimeoutRef.current);
                endAudioRetryTimeoutRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const onVisibility = () => {
            requestAnimationFrame(() => {
                const s = spinStateRef.current;
                if (!s.isSpinning || s.hasCompleted || !s.spinWallStartMs) return;
                const now = performance.now();
                const elapsedMs = Math.max(0, now - s.spinWallStartMs);
                const progress = Math.min(1, Math.max(0, elapsedMs / s.durationMs));
                const easedProgress = 1 - Math.pow(1 - progress, 3);
                s.ang = ((s.startAng + (s.targetAng - s.startAng) * easedProgress) % TAU_MATH + TAU_MATH) % TAU_MATH;
                drawWheelRef.current(s.ang);
                if (progress >= 1) {
                    completeSpinRef.current();
                }
            });
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        const updateDevicePixelRatio = () => {
            const nextRatio = window.devicePixelRatio || 1;
            setDevicePixelRatio((prevRatio) => (Math.abs(prevRatio - nextRatio) > 0.001 ? nextRatio : prevRatio));
        };
        window.addEventListener("resize", updateDevicePixelRatio);
        const viewport = window.visualViewport;
        if (viewport) {
            viewport.addEventListener("resize", updateDevicePixelRatio);
        }
        return () => {
            window.removeEventListener("resize", updateDevicePixelRatio);
            if (viewport) {
                viewport.removeEventListener("resize", updateDevicePixelRatio);
            }
        };
    }, []);

    const truncateFromStart = (text, maxLength = 20) => {
        if (text.length <= maxLength) return text;
        return '...' + text.slice(-(maxLength - 3));
    };

    const labelFontPx = Math.max(12, Math.round(14 * (dia / 400)));

    const drawSector = (sector, i, angleOffset = 0) => {
        const ctx = ctxRef.current;
        if (!ctx) return;
        const ang = arc * i + angleOffset;
        ctx.save();
        ctx.beginPath();
        ctx.fillStyle = sector.color;
        ctx.moveTo(rad, rad);
        ctx.arc(rad, rad, rad, ang, ang + arc);
        ctx.lineTo(rad, rad);
        ctx.fill();

        ctx.translate(rad, rad);
        ctx.rotate(ang + arc / 2);
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        ctx.lineJoin = "round";
        ctx.lineWidth = Math.max(1, Math.round(labelFontPx / 6));
        ctx.strokeStyle = "rgba(0, 0, 0, 0.65)";
        ctx.fillStyle = "#fff";
        ctx.font = `700 ${labelFontPx}px "Inter", "Helvetica Neue", Arial, sans-serif`;
        const text = truncateFromStart(sector?.label || "");
        const textX = Math.round(rad - 12);
        ctx.strokeText(text, textX, 0);
        ctx.fillText(text, textX, 0);
        ctx.restore();
    };

    const drawWheel = (angle = 0) => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!canvas || !ctx) return;
        const wheelBuffer = wheelBufferRef.current;
        if (!wheelBuffer) return;
        ctx.clearRect(0, 0, logicalWheelSize, logicalWheelSize);
        ctx.save();
        ctx.translate(rad, rad);
        ctx.rotate(angle - PI / 2);
        ctx.drawImage(wheelBuffer, -rad, -rad, logicalWheelSize, logicalWheelSize);
        ctx.restore();
    };

    const getIndex = (angle) => {
        if (tot <= 0) return 0;
        const a = ((angle % TAU) + TAU) % TAU;
        return Math.floor(tot - (a / TAU) * tot) % tot;
    };

    const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);
    const playEndAudio = () => {
        const endAudio = endAudioRef.current;
        const fallbackAudio = endAudioFallbackRef.current;
        if (!isActiveRef.current || !endAudio) return;
        if (endAudioRetryTimeoutRef.current != null) {
            clearTimeout(endAudioRetryTimeoutRef.current);
            endAudioRetryTimeoutRef.current = null;
        }
        const playFallback = () => {
            if (!fallbackAudio) return;
            try {
                fallbackAudio.currentTime = 0;
                fallbackAudio.play().catch(() => {});
            } catch (error) {
                return;
            }
        };
        const doPlay = () => {
            endAudio.stop();
            const soundId = endAudio.play();
            if (soundId == null) {
                playFallback();
                return;
            }
            endAudio.once("playerror", () => {
                playFallback();
            }, soundId);
            endAudioRetryTimeoutRef.current = setTimeout(() => {
                endAudioRetryTimeoutRef.current = null;
                if (!endAudio.playing(soundId)) {
                    playFallback();
                }
            }, 160);
        };
        if (Howler.ctx && Howler.ctx.state === "suspended") {
            Howler.ctx.resume().then(doPlay).catch(() => {
                doPlay();
            });
            return;
        }
        doPlay();
    };

    const completeSpin = () => {
        const state = spinStateRef.current;
        if (state.hasCompleted) return;
        state.hasCompleted = true;
        state.isSpinning = false;
        state.spinWallStartMs = 0;
        state.prevAngForAudio = state.ang;
        state.audioLastTs = 0;
        if (spinCompletionTimeoutRef.current != null) {
            clearTimeout(spinCompletionTimeoutRef.current);
            spinCompletionTimeoutRef.current = null;
        }
        if (state.animFrame != null) {
            cancelAnimationFrame(state.animFrame);
            state.animFrame = null;
        }
        const runningAudio = runningAudioRef.current;
        if (runningAudio) {
            runningAudio.stop();
            runningAudio.rate(1);
            runningAudio.volume(1);
        }
        playEndAudio();
        const idx = getIndex(state.ang);
        const sector = segments[idx];
        onFinished(sector?.label);
        setResult(sector?.label);
        setTimeout(() => setResult(null), 2000);
    };

    const frame = () => {
        const state = spinStateRef.current;
        if (!state.isSpinning) return;
        const now = performance.now();
        if (!state.audioLastTs) {
            state.prevAngForAudio = state.ang;
            state.audioLastTs = now;
        }
        const elapsedMs = Math.max(0, now - state.spinWallStartMs);
        const progress = Math.max(0, Math.min(1, elapsedMs / state.durationMs));
        const easedProgress = easeOutCubic(progress);
        state.ang = ((state.startAng + (state.targetAng - state.startAng) * easedProgress) % TAU + TAU) % TAU;
        if (now - state.audioLastTs >= AUDIO_UPDATE_INTERVAL_MS) {
            const runningAudio = runningAudioRef.current;
            if (runningAudio) {
                const deltaAngle = Math.abs(state.ang - state.prevAngForAudio);
                const wrappedDelta = Math.min(deltaAngle, TAU - deltaAngle);
                const deltaMs = Math.max(1, now - state.audioLastTs);
                const angularVelocityPerFrame = (wrappedDelta / deltaMs) * (1000 / 60);
                const normalizedSpeed = Math.min(1, angularVelocityPerFrame / MAX_EXPECTED_ANGULAR_VELOCITY);
                const playbackRate = PLAYBACK_RATE_MIN + normalizedSpeed * (PLAYBACK_RATE_MAX - PLAYBACK_RATE_MIN);
                runningAudio.rate(Math.max(PLAYBACK_RATE_MIN, Math.min(PLAYBACK_RATE_MAX, playbackRate)));
                if (normalizedSpeed < VOLUME_FADE_THRESHOLD) {
                    runningAudio.volume(Math.max(0, normalizedSpeed / VOLUME_FADE_THRESHOLD));
                } else {
                    runningAudio.volume(1);
                }
            }
            state.prevAngForAudio = state.ang;
            state.audioLastTs = now;
        }
        drawWheel(state.ang);
        if (progress >= 1) {
            completeSpin();
        }
    };

    const engine = () => {
        frame();
        const s = spinStateRef.current;
        if (!s.isSpinning) {
            s.animFrame = null;
            return;
        }
        s.animFrame = requestAnimationFrame(engine);
    };

    const drawer = () => {
        if (spinStateRef.current.isSpinning || result || (newStudentList?.length === 0 || !newStudentList)) return;
        const startSpin = () => {
            const state = spinStateRef.current;
            if (state.animFrame != null) {
                cancelAnimationFrame(state.animFrame);
                state.animFrame = null;
            }
            state.isSpinning = true;
            state.hasCompleted = false;
            state.durationMs = SPIN_DURATION_MS;
            state.spinWallStartMs = performance.now();
            state.audioLastTs = 0;
            state.startAng = ((state.ang % TAU) + TAU) % TAU;
            state.targetAng = state.startAng + TAU * rand(MIN_FULL_ROTATIONS, MAX_FULL_ROTATIONS) + rand(0, TAU);
            state.prevAngForAudio = state.startAng;
            if (spinCompletionTimeoutRef.current != null) {
                clearTimeout(spinCompletionTimeoutRef.current);
                spinCompletionTimeoutRef.current = null;
            }
            spinCompletionTimeoutRef.current = setTimeout(() => {
                spinCompletionTimeoutRef.current = null;
                const st = spinStateRef.current;
                if (!st.isSpinning || st.hasCompleted) return;
                st.ang = ((st.startAng + (st.targetAng - st.startAng)) % TAU + TAU) % TAU;
                drawWheelRef.current(st.ang);
                completeSpinRef.current();
            }, state.durationMs);
            const runningAudio = runningAudioRef.current;
            if (runningAudio) {
                runningAudio.stop();
                runningAudio.rate(PLAYBACK_RATE_MAX);
                runningAudio.volume(1);
                runningAudio.play();
            }
            state.animFrame = requestAnimationFrame(engine);
        };

        if (Howler.ctx && Howler.ctx.state === "suspended") {
            Howler.ctx.resume().then(startSpin).catch(() => {
                startSpin();
            });
            return;
        }
        startSpin();
    };

    const handleTouchDrawer = (event) => {
        event.preventDefault();
        drawer()
    }

    useLayoutEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctxRef.current = ctx;
        const renderScale = Math.min(MAX_RENDER_SCALE, Math.max(1, devicePixelRatio * TEXT_SUPERSAMPLE_FACTOR));
        const px = Math.max(1, Math.round(logicalWheelSize * renderScale));
        canvas.width = px;
        canvas.height = px;
        canvas.style.width = `${logicalWheelSize}px`;
        canvas.style.height = `${logicalWheelSize}px`;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.scale(renderScale, renderScale);
        ctx.imageSmoothingEnabled = true;
        if ("imageSmoothingQuality" in ctx) {
            ctx.imageSmoothingQuality = "high";
        }
        const wheelBuffer = document.createElement("canvas");
        wheelBuffer.width = px;
        wheelBuffer.height = px;
        const wheelBufferCtx = wheelBuffer.getContext("2d");
        if (!wheelBufferCtx) return;
        wheelBufferCtx.setTransform(1, 0, 0, 1, 0, 0);
        wheelBufferCtx.scale(renderScale, renderScale);
        wheelBufferCtx.imageSmoothingEnabled = true;
        if ("imageSmoothingQuality" in wheelBufferCtx) {
            wheelBufferCtx.imageSmoothingQuality = "high";
        }
        const previousCtx = ctxRef.current;
        ctxRef.current = wheelBufferCtx;
        wheelBufferCtx.clearRect(0, 0, logicalWheelSize, logicalWheelSize);
        if (segments?.length) {
            segments.forEach((sector, i) => drawSector(sector, i, 0));
        }
        ctxRef.current = previousCtx;
        wheelBufferRef.current = wheelBuffer;
        drawWheel(spinStateRef.current.ang);
    }, [segments, logicalWheelSize, arc, rad, labelFontPx, devicePixelRatio]);

    drawWheelRef.current = drawWheel;
    completeSpinRef.current = completeSpin;

    return (
        <div id="newWheelOfFortune" onClick={drawer} onTouchStart={handleTouchDrawer}>
            {result
             ? (
                <div  style={{width: "100%" ,height: logicalWheelSize}} id="newWheelOfFortuneResult">
                    <div  id="newWheelOfFortuneResultText">{result}</div>
                </div>
            ) : null}
            <canvas style={{ width: logicalWheelSize, height: logicalWheelSize }} ref={canvasRef} id="newWheel"></canvas>
            <div id="newSpin">SPIN</div>
        </div>
    );
};

export default WheelComponent;